import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FiInfo, FiDownload, FiUpload, } from "react-icons/fi";
import { MdEdit, MdAdd, MdSearch } from "react-icons/md";
import { Trash2 } from "lucide-react";
import { ImCross } from "react-icons/im";
import {
  Spinner,
  GenericTable,
  Pagination,
  SearchableDropdown,
  DownloadExcelBill,
  Tooltip,
  FileUploader,
} from "../../../Components/Common";
import { useProductMaster } from "../Hooks/useProductMaster";
import {
  PRODUCTS_COLUMNS,
  PRODUCTS_COLUMN_LABELS,
  PRODUCT_COLUMN_WIDTHS,
  PRODUCT_EXCEL_COLUMNS,
} from "../constants";
import { CREATE_PRODUCT_MASTER, EDIT_PRODUCT_MASTER } from "../../../routes";
import { getProducts } from "../../../Services/productService";

const ProductMasterList = () => {
  const navigate = useNavigate();
  const {
    loading,
    filters,
    setFilters,
    page,
    totalPages,
    rowsPerPage,
    setPage,
    setRowsPerPage,
    onPageChange,
    fetchProducts,
    deleteProductData,
    fetchCategories,
    uploadProductsFile,
  } = useProductMaster();

  const [products, setProducts] = useState([]);

  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedViewProduct, setSelectedViewProduct] = useState(null);

  const fetchData = async () => {
    const data = await fetchProducts();
    if (data) setProducts(data);
  };

  const handleUploadBulk = async (file) => {
    await uploadProductsFile(file);
    fetchData();
  };

  const handleFilterChange = (key, value) => {
    const updated = { ...filters, [key]: value };
    setFilters(updated);
    onPageChange(1);
  };

  const handleViewProduct = (product) => {
    setSelectedViewProduct(product);
    setIsViewModalOpen(true);
  };

  const handleEditProduct = (product) => {
    const path = EDIT_PRODUCT_MASTER.replace(":instanceId", product.id);
    navigate(path);
  };

  const handleDeleteProduct = async (product) => {
    const confirmDelete = window.confirm(
      `Are you sure you want to delete "${product.product_name}"?`
    );
    if (!confirmDelete) return;

    const success = await deleteProductData(product.id);
    if (success) {
      setProducts((prev) => {
        const updated = prev.filter((p) => p.id !== product.id);
        if (updated.length === 0 && page > 1) {
          setPage(page - 1);
        }
        return updated;
      });
    }
  };

  const handleDownloadExcel = () => {
    DownloadExcelBill({
      fetchFn: getProducts,
      fieldMappings: PRODUCT_EXCEL_COLUMNS,
      fileName: "Product_Master.xlsx",
      filters: filters,
    });
  };

  const renderField = (key, row) => {
    const content = (() => {
      switch (key) {
        case "product_name":
          return (
            <span className="font-medium text-gray-900">
              {row.product_name}
            </span>
          );

        case "specification":
          return <span>{row.specification || "-"}</span>;
        
        case "make":
          return <span>{row.make || "-"}</span>;

        case "SKU":
          return <span>{row.SKU || "-"}</span>;

        case "UOM":
          return <span>{row.UOM_name || "-"}</span>;

        case "category_name":
          return <span>{row.category_name || "-"}</span>;

        case "actions":
          return (
            <div className="flex gap-6">
              <FiInfo
                size={20}
                onClick={() => handleViewProduct(row)}
                className="text-blue-600 cursor-pointer"
              />
              <MdEdit
                size={20}
                onClick={() => handleEditProduct(row)}
                className="text-primary cursor-pointer"
              />

              <Trash2
                size={20}
                onClick={() => handleDeleteProduct(row)}
                className="text-red-600 cursor-pointer"
              />
            </div>
          );

        default:
          return row[key] ?? "-";
      }
    })();

    return (
      <div className={`${PRODUCT_COLUMN_WIDTHS[key] || ""}`}>{content}</div>
    );
  };

  const renderMobileFields = (row) => {
    return (
      <div
        key={row.id}
        className="border-b-2 rounded-md p-3 mb-3 bg-white hover:bg-gray-50 transition-all duration-200"
        onClick={() => handleViewProduct(row)}
      >
        <div className="flex flex-col gap-1">
          <div className="font-semibold text-gray-900 text-base truncate max-w-[90%]">
            {row.product_name || "-"}
          </div>

          <div className="grid grid-cols-4 gap-2 items-center">
            <div className="col-span-2 text-gray-600 text-sm truncate max-w-[90%]">
              <span className="font-medium">Specification: </span>
              {row.specification || "-"}
            </div>

            <div className="text-gray-600 text-sm truncate">
              {row.category_name || "-"}
            </div>

            <div
              className="flex items-center gap-4 justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <MdEdit
                size={18}
                className="text-primary cursor-pointer"
                onClick={() => handleEditProduct(row)}
              />
              <Trash2
                size={18}
                className="text-red-600 cursor-pointer"
                onClick={() => handleDeleteProduct(row)}
              />
            </div>
          </div>

          <div className="flex text-gray-600 text-sm gap-2">
            <div className="truncate">
              <span className="font-medium">SKU: </span>
              {row.SKU || "-"}
            </div>
            <div>|</div>
            <div className="truncate">
              <span className="font-medium">UOM: </span>
              {row.UOM_name || "-"}
            </div>
          </div>
        </div>
      </div>
    );
  };

  useEffect(() => {
    fetchData();
  }, [filters, page, rowsPerPage]);

  return (
    <div className="flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between md:gap-4 gap-2">
        <div className="flex-1 pl-6">
          <h1 className="text-black text-xl md:text-2xl font-semibold pt-2 md:py-3 whitespace-nowrap">
            Product Master
          </h1>
        </div>

        <div className="flex items-center gap-3 m-5 mb-3">
          <Tooltip text="Download Excel">
            <button
              onClick={handleDownloadExcel}
              className="px-4 md:py-[9px] py-2 bg-primary text-white rounded-md hover:bg-secondary transition-colors"
            >
              <FiDownload size={20}/>
            </button>
          </Tooltip>
          <Tooltip text="Upload Excel">
            <FileUploader
              label=""
              helperText=""
              accept=".xlsx,.xls"
              singleButton={true}              
              btnText={<FiUpload size={18} />} 
              onUploadClick={(file) => handleUploadBulk(file)}
              isUploading={loading}
            />
          </Tooltip>
          <button
            onClick={() => navigate(CREATE_PRODUCT_MASTER)}
            className="px-4 py-2 gap-2 rounded-md flex items-center bg-primary text-white hover:bg-blue-700"
          >
            <MdAdd size={20} />
            <span className="hidden md:inline md:whitespace-nowrap text-sm md:text-base">
              Add Product
            </span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white w-full rounded-2xl shadow-md border px-4 py-3">
        <div className="flex gap-2">
          <div className="relative w-1/2">
            <MdSearch
              size={20}
              className="absolute md:left-2 mt-2 left-2 text-gray-500"
            />
            <input
              type="text"
              name="search"
              value={filters.search}
              onChange={(e) => handleFilterChange("search", e.target.value)}
              placeholder="Search by product name"
              className="w-full rounded-lg border border-gray-300 bg-white text-sm pl-9 pr-8 placeholder-gray-400 focus:ring-1 focus:border-blue-500 outline-none transition duration-150"
            />
            {filters.search && (
              <button
                type="button"
                onClick={() => handleFilterChange("search", "")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            )}
          </div>
          <div className="w-1/2">
            <SearchableDropdown
              fetchFunction={fetchCategories}
              onSelect={(val) => setFilters({ ...filters, category: val })}
              valueKey="id"
              labelKey="name"
              includeAllOption
              allOptionLabel="All Categories"
              placeholder={"Select Category"}
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-x-auto sm:rounded-lg mt-2">
        {loading ? (
          <div className="flex items-center justify-center h-[60vh]">
            <Spinner />
          </div>
        ) : (
          <div className="w-full flex flex-col h-[61vh] md:h-[48vh] overflow-x-auto px-1">
            <GenericTable
              tableColumns={PRODUCTS_COLUMNS}
              tableRows={products}
              columnLabels={PRODUCTS_COLUMN_LABELS}
              renderField={renderField}
              enableGridLayout={true}
              mobileGridColumns={4}
              renderMobileCard={renderMobileFields}
            />
          </div>
        )}

        {products.length > 0 && (
          <div className="flex justify-end pb-4 px-4 mt-2">
            <Pagination
              page={page}
              totalPages={totalPages}
              rowsPerPage={rowsPerPage}
              onPageChange={setPage}
              onPageSizeChange={setRowsPerPage}
            />
          </div>
        )}
      </div>

      {/* View Product Modal */}
      {isViewModalOpen && selectedViewProduct && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-3">
          <div className="bg-white rounded-xl shadow-xl p-3 w-full max-w-2xl relative">
            <button
              onClick={() => setIsViewModalOpen(false)}
              className="absolute top-3 right-3 w-8 h-6 flex items-center justify-center rounded-sm bg-red-500 text-gray-700 font-bold hover:bg-red-700 hover:text-gray-800"
            >
              <ImCross size={10} />
            </button>

            <div className="px-6 py-2 border-b">
              <h2 className="text-2xl font-bold">Product Details</h2>
            </div>

            <div className="px-6 py-4 space-y-4">
              <div>
                <h3 className="text-lg font-semibold mb-1">Product Name</h3>
                <p className="text-gray-700">
                  {selectedViewProduct.product_name || "-"}
                </p>
              </div>

              <div className="flex flex-col md:flex-row md:justify-between gap-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold mb-1">Specification</h3>
                  <p className="text-gray-700">
                    {selectedViewProduct.specification || "-"}
                  </p>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold mb-1">Category</h3>
                  <p className="text-gray-700">
                    {selectedViewProduct.category_name || "-"}
                  </p>
                </div>
              </div>

              <div className="flex flex-col md:flex-row md:justify-between gap-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold mb-1">
                    Stock Keeping Units
                  </h3>
                  <p className="text-gray-700">
                    {selectedViewProduct.SKU || "-"}
                  </p>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold mb-1">
                    Unit of Measurement
                  </h3>
                  <p className="text-gray-700">
                    {selectedViewProduct.UOM_name || "-"}
                  </p>
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold mb-1">Brand</h3>
                <p className="text-gray-700">
                  {selectedViewProduct.make || "-"}
                </p>
              </div> 

              {/* Product Attributes */}
              <div className="mt-4">
                <h3 className="text-lg font-semibold mb-2 border-b pb-1">
                  Product Attributes
                </h3>

                {selectedViewProduct.attributes &&
                selectedViewProduct.attributes.length > 0 ? (
                  <div className="border rounded-md max-h-[40vh] overflow-y-auto">
                    <table className="w-full text-sm text-left border-collapse">
                      <thead className="bg-gray-100 sticky top-0">
                        <tr className="border-b">
                          <th className="px-4 py-2 font-semibold text-gray-700 w-1/2">
                            Name
                          </th>
                          <th className="px-4 py-2 font-semibold text-gray-700 w-1/2">
                            Value
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedViewProduct.attributes.map((attr) => (
                          <tr
                            key={attr.id}
                            className="border-b hover:bg-gray-50 transition-colors duration-150"
                          >
                            <td className="px-4 py-2 text-gray-800">
                              {attr.name}
                            </td>
                            <td className="px-4 py-2 text-gray-600">
                              {attr.value}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-gray-500 italic">
                    No attributes present for this product.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductMasterList;
